// Client-side fallback for image attachments that are too large for
// the Edge Function to accept (or that consistently fail to send).
// Re-reads the data URL through a canvas, scales it down to fit
// `maxDim`, and re-encodes as JPEG at the given quality. Returns a
// smaller data URL; falls back to the original on any error.
export async function downscaleDataUrl(
  dataUrl: string,
  maxDim = 1280,
  quality = 0.82,
): Promise<string> {
  if (!dataUrl?.startsWith("data:image/")) return dataUrl;
  try {
    const img = await loadImage(dataUrl);
    const { width, height } = img;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    if (scale >= 1 && dataUrl.length < 1_500_000) return dataUrl;
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return dataUrl;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}